import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input/Input';
import { Textarea } from '../../ui/Textarea/Textarea';
import { CheckIcon, PencilIcon, XIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export type EditableFieldVariant = 'text' | 'email' | 'phone' | 'textarea';

export type EditableFieldSize = 'sm' | 'md' | 'lg';

const SIZE_DISPLAY_CLASS: Record<EditableFieldSize, string> = {
  sm: 'text-sm',
  md: 'text-base font-medium',
  lg: 'text-lg font-semibold',
};

const SIZE_INPUT_CLASS: Record<EditableFieldSize, string> = {
  sm: '',
  md: 'text-base',
  lg: 'text-lg font-semibold h-11',
};

export interface EditableFieldHandle {
  /** Programmatically enter edit mode (used to focus the field). */
  startEdit: () => void;
}

export interface EditableFieldProps {
  /** Optional small caption above the field. */
  label?: string;
  /** Currently persisted value. */
  value?: string;
  /** Field variant. `textarea` renders a multi-line input. */
  variant?: EditableFieldVariant;
  /**
   * Visual size for the displayed value and (when editing) the input.
   * Defaults to `sm`. Use `lg` for hero headings.
   */
  size?: EditableFieldSize;
  placeholder?: string;
  /** Read-mode fallback when `value` is empty/nullish. Defaults to '—'. */
  emptyDisplay?: ReactNode;
  /**
   * Persist handler. Called with the trimmed next value (or '' when cleared).
   * Throw / reject to surface an error toast and keep edit mode open.
   */
  onSave: (next: string) => Promise<void> | void;
  /**
   * Optional sync validator. Returns an error string to block the save, or
   * null when valid.
   */
  validate?: (next: string) => string | null;
  /** When true the pencil affordance is hidden and editing disabled. */
  disabled?: boolean;
  /** Hide the rendered label even when `label` is provided. */
  hideLabel?: boolean;
  /** Optional aria-label override for the edit pencil. */
  ariaLabel?: string;
  /** Optional className applied to the root container. */
  className?: string;
}

/**
 * Inline-editable scalar primitive — used by the client workspace for simple
 * fields like name / domain / phone / email / AI prompt that don't warrant
 * their own modal.
 *
 * Behaviour:
 * - Display mode: shows value (or italic empty state) + a pencil button on hover.
 * - Edit mode: focus-trapped Input/Textarea + Save / Cancel buttons.
 * - Enter saves (Cmd/Ctrl+Enter for textarea); Escape cancels.
 * - Sync `validate` runs before `onSave`; async errors thrown by `onSave`
 *   surface as a toast and keep the editor open.
 */
export const EditableField = forwardRef<EditableFieldHandle, EditableFieldProps>(
  function EditableField(
    {
      label,
      value,
      variant = 'text',
      size = 'sm',
      placeholder,
      emptyDisplay = '—',
      onSave,
      validate,
      disabled,
      hideLabel,
      ariaLabel,
      className,
    },
    ref,
  ) {
    const { t } = useTranslation(['m-client']);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Sync draft when `value` changes externally (e.g. after a successful
    // save the parent re-renders with the new value).
    useEffect(() => {
      if (!editing) setDraft(value ?? '');
    }, [value, editing]);

    // Auto-focus + caret-to-end when entering edit mode.
    useEffect(() => {
      if (!editing) return;
      const el = variant === 'textarea' ? textareaRef.current : inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        // some input types (e.g. email) reject setSelectionRange — ignore.
      }
    }, [editing, variant]);

    const startEdit = useCallback(() => {
      if (disabled) return;
      setDraft(value ?? '');
      setError(null);
      setEditing(true);
    }, [disabled, value]);

    useImperativeHandle(ref, () => ({ startEdit }), [startEdit]);

    const handleCancel = useCallback(() => {
      setEditing(false);
      setError(null);
      setDraft(value ?? '');
    }, [value]);

    const handleSave = useCallback(async () => {
      const next = draft.trim();
      if (next === (value ?? '').trim()) {
        setEditing(false);
        setError(null);
        return;
      }
      if (validate) {
        const message = validate(next);
        if (message) {
          setError(message);
          return;
        }
      }
      try {
        setSaving(true);
        await onSave(next);
        setEditing(false);
        setError(null);
        toast.success(t('inline_edit.save_success'));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        toast.error(t('inline_edit.save_error', { message }));
      } finally {
        setSaving(false);
      }
    }, [draft, onSave, t, validate, value]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (variant === 'textarea') {
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            void handleSave();
          }
        } else {
          e.preventDefault();
          void handleSave();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    const inputType =
      variant === 'email' ? 'email' : variant === 'phone' ? 'tel' : 'text';

    const computedAriaLabel =
      ariaLabel ??
      (label
        ? t('inline_edit.edit_aria', { field: label })
        : t('inline_edit.save'));

    return (
      <div className={cn('space-y-1', className)}>
        {label && !hideLabel ? (
          <div className="text-xs font-medium text-muted-foreground">
            {label}
          </div>
        ) : null}

        {editing ? (
          <div className="space-y-2">
            {variant === 'textarea' ? (
              <Textarea
                ref={textareaRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={4}
                disabled={saving}
                className={cn(SIZE_INPUT_CLASS[size])}
              />
            ) : (
              <Input
                ref={inputRef}
                type={inputType}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={saving}
                className={cn(SIZE_INPUT_CLASS[size])}
              />
            )}
            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : null}
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                <CheckIcon className="h-3.5 w-3.5" />
                {t('inline_edit.save')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={saving}>
                <XIcon className="h-3.5 w-3.5" />
                {t('inline_edit.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="group flex items-start gap-2">
            <div className={cn('flex-1 min-w-0', SIZE_DISPLAY_CLASS[size])}>
              {value ? (
                variant === 'textarea' ? (
                  <p className="whitespace-pre-wrap text-gray-900 leading-relaxed">
                    {value}
                  </p>
                ) : (
                  <span className="text-gray-900 break-words">{value}</span>
                )
              ) : (
                <span className="text-gray-400 italic">{emptyDisplay}</span>
              )}
            </div>
            {!disabled ? (
              <button
                type="button"
                onClick={startEdit}
                aria-label={computedAriaLabel}
                className="flex-shrink-0 mt-0.5 p-1 text-muted-foreground hover:text-gray-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  },
);
